import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import InputSection from './InputSection';
import ResultsSection from './ResultsSection';
import ComparisonChart from './ComparisonChart';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHome, FiCalculator, FiTrendingUp } = FiIcons;

const MortgageCalculator = () => {
  const [currentMortgage, setCurrentMortgage] = useState({
    term: 30,
    rate: 6.5,
    amount: 350000
  });

  const [proposedRefi, setProposedRefi] = useState({
    term: 30,
    rate: 5.8,
    amount: 350000
  });

  const [results, setResults] = useState(null);

  const calculateMortgage = (principal, rate, termYears) => {
    const monthlyRate = rate / 100 / 12;
    const numPayments = termYears * 12;
    
    const monthlyPayment = principal * 
      (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
      (Math.pow(1 + monthlyRate, numPayments) - 1);
    
    const totalInterest = (monthlyPayment * numPayments) - principal;
    
    return {
      monthlyPayment,
      totalInterest,
      totalPaid: monthlyPayment * numPayments
    };
  };

  // Check if all required fields have valid values
  const isFormComplete = () => {
    return currentMortgage.amount > 0 && 
           currentMortgage.rate > 0 && 
           currentMortgage.term > 0 &&
           proposedRefi.amount > 0 && 
           proposedRefi.rate > 0 && 
           proposedRefi.term > 0;
  };

  useEffect(() => {
    if (isFormComplete()) {
      const current = calculateMortgage(
        currentMortgage.amount,
        currentMortgage.rate,
        currentMortgage.term
      );
      
      const refi = calculateMortgage(
        proposedRefi.amount,
        proposedRefi.rate,
        proposedRefi.term
      );

      setResults({
        current,
        refi,
        monthlyDifference: refi.monthlyPayment - current.monthlyPayment,
        totalInterestDifference: refi.totalInterest - current.totalInterest
      });
    } else {
      setResults(null);
    }
  }, [currentMortgage, proposedRefi]);

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center items-center gap-3 mb-4">
            <SafeIcon icon={FiHome} className="text-4xl text-primary-600" />
            <h1 className="text-4xl font-bold text-gray-900">
              Mortgage vs Refinance Calculator
            </h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Compare your current mortgage with a potential refinance to see potential savings
          </p>
        </motion.div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Input Sections */}
          <div className="xl:col-span-2 space-y-8">
            <InputSection
              title="Current Mortgage"
              icon={FiCalculator}
              data={currentMortgage}
              onChange={setCurrentMortgage}
              color="primary"
            />
            
            <InputSection
              title="Proposed Refinance"
              icon={FiTrendingUp}
              data={proposedRefi}
              onChange={setProposedRefi}
              color="success"
            />
          </div>

          {/* Results Section */}
          <div className="space-y-8">
            {results ? (
              <>
                <ResultsSection results={results} />
                <ComparisonChart results={results} />
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-center"
              >
                <SafeIcon icon={FiCalculator} className="text-4xl text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  Enter All Values
                </h3>
                <p className="text-gray-500">
                  Please fill in all mortgage details to see the comparison results
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MortgageCalculator;